import axiosInstance from './axios';

export interface Test {
  uuid: string;
  name: string;
  email: string;
  date_created: string;
  owner_id: string;
  time_limit_minutes: number; // Time limit in minutes (default: 45)
  deleted_at?: string;
  ttl?: number;
}

export interface Question {
  test_uuid: string;
  id: string;
  content: string;
  date_created: string;
  is_html: boolean;
  order: number;
}

export interface Interview {
  uuid: string;
  test_uuid: string;
  status: 'invited' | 'pending' | 'started' | 'completed' | 'deleted';
  is_demo: boolean;
  date_created: string;
  date_started?: string;
  date_completed?: string;
  name: string;
  email: string;
  unlock_requested?: boolean;
  unlock_approved?: boolean;
  unlock_reason?: string;
  unlock_requested_at?: string;
  unlock_approved_at?: string;
  privacy_expired_at?: string;
  deleted_at?: string;
  ttl?: number;
}

export interface Answer {
  interview_uuid: string;
  question_id: string;
  content: string;
  recorder: string;
  frames: string;
  duration: number;
  date_created: string;
  uuid: string;
  metadata?: { [key: string]: string };
}

// Test API calls
export const testAPI = {
  getAll: async (): Promise<Test[]> => {
    const response = await axiosInstance.get('/tests');
    return response.data.data || response.data;
  },

  getById: async (uuid: string): Promise<Test> => {
    if (!uuid || uuid === 'undefined' || uuid === 'null') {
      throw new Error('Invalid test UUID provided');
    }
    const response = await axiosInstance.get(`/tests/${uuid}`);
    return response.data.data || response.data;
  },

  create: async (test: { name: string; time_limit_minutes?: number }): Promise<Test> => {
    const response = await axiosInstance.post('/tests', test);
    return response.data.data || response.data;
  },

  update: async (uuid: string, test: { name: string; time_limit_minutes?: number }): Promise<Test> => {
    if (!uuid || uuid === 'undefined' || uuid === 'null') {
      throw new Error('Invalid test UUID provided');
    }
    const response = await axiosInstance.put(`/tests/${uuid}`, test);
    return response.data.data || response.data;
  },

  delete: async (uuid: string): Promise<void> => {
    if (!uuid || uuid === 'undefined' || uuid === 'null') {
      throw new Error('Invalid test UUID provided');
    }
    await axiosInstance.delete(`/tests/${uuid}`);
  },

  getDashboard: async (): Promise<any> => {
    const response = await axiosInstance.get('/dashboard');
    return response.data;
  }
};

// Question API calls
export const questionAPI = {
  getByTestUuid: async (testUuid: string): Promise<Question[]> => {
    if (!testUuid || testUuid === 'undefined' || testUuid === 'null') {
      throw new Error('Invalid test UUID provided');
    }
    const response = await axiosInstance.get(`/tests/${testUuid}/questions`);
    return response.data.data || response.data;
  },

  create: async (testUuid: string, question: { content: string; is_html: boolean; order: number }): Promise<Question> => {
    if (!testUuid || testUuid === 'undefined' || testUuid === 'null') {
      throw new Error('Invalid test UUID provided');
    }
    const response = await axiosInstance.post(`/tests/${testUuid}/questions`, question);
    return response.data.data || response.data;
  },

  update: async (questionId: string, question: { content: string; is_html: boolean; order: number }, testUuid: string): Promise<Question> => {
    if (!questionId || questionId === 'undefined' || questionId === 'null') {
      throw new Error('Invalid question ID provided');
    }
    if (!testUuid || testUuid === 'undefined' || testUuid === 'null') {
      throw new Error('Invalid test UUID provided');
    }
    const response = await axiosInstance.put(`/questions/${questionId}?test_uuid=${testUuid}`, question);
    return response.data.data || response.data;
  },

  delete: async (questionId: string, testUuid: string): Promise<void> => {
    if (!questionId || questionId === 'undefined' || questionId === 'null') {
      throw new Error('Invalid question ID provided');
    }
    if (!testUuid || testUuid === 'undefined' || testUuid === 'null') {
      throw new Error('Invalid test UUID provided');
    }
    await axiosInstance.delete(`/questions/${questionId}?test_uuid=${testUuid}`);
  }
};

// Interview API calls
export const interviewAPI = {
  getByTestUuid: async (testUuid: string): Promise<Interview[]> => {
    if (!testUuid || testUuid === 'undefined' || testUuid === 'null') {
      throw new Error('Invalid test UUID provided');
    }
    const response = await axiosInstance.get(`/tests/${testUuid}/interviews`);
    return response.data.data || response.data;
  },

  getById: async (uuid: string): Promise<Interview> => {
    if (!uuid || uuid === 'undefined' || uuid === 'null') {
      throw new Error('Invalid interview UUID provided');
    }
    const response = await axiosInstance.get(`/interviews/${uuid}`);
    return response.data.data || response.data;
  },

  create: async (testUuid: string, interview: { name: string; email: string; is_demo?: boolean }): Promise<Interview> => {
    const response = await axiosInstance.post(`/tests/${testUuid}/interviews`, interview);
    return response.data.data || response.data;
  },

  invite: async (testUuid: string, invitation: { name: string; email: string; personal_message?: string; cc_emails?: string[] }): Promise<{ message: string; interview: Interview; email: string; sent_at: string }> => {
    const response = await axiosInstance.post(`/tests/${testUuid}/invite`, invitation);
    return response.data.data || response.data;
  },

  updateStatus: async (uuid: string, status: 'started' | 'completed'): Promise<Interview> => {
    const response = await axiosInstance.put(`/interviews/${uuid}`, { status });
    return response.data.data || response.data;
  },

  delete: async (uuid: string): Promise<{ message: string; uuid: string; deleted_at: string; ttl_expires: string }> => {
    if (!uuid || uuid === 'undefined' || uuid === 'null') {
      throw new Error('Invalid interview UUID provided');
    }
    const response = await axiosInstance.delete(`/interviews/${uuid}`);
    return response.data.data || response.data;
  },

  deleteBulk: async (uuids: string[]): Promise<{ message: string; deleted_count: number; failed_count: number; results: any[] }> => {
    if (!uuids || uuids.length === 0) {
      throw new Error('No interview UUIDs provided');
    }
    
    // Since we don't have a bulk delete endpoint, delete them individually
    const results = [];
    let deleted_count = 0;
    let failed_count = 0;
    
    for (const uuid of uuids) {
      try {
        const result = await this.delete(uuid);
        results.push({ uuid, success: true, data: result });
        deleted_count++;
      } catch (error) {
        results.push({ uuid, success: false, error: error instanceof Error ? error.message : 'Unknown error' });
        failed_count++;
      }
    }
    
    return {
      message: `Deleted ${deleted_count} interviews, ${failed_count} failed`,
      deleted_count,
      failed_count,
      results
    };
  }
};

// Answer API calls
export const answerAPI = {
  getByInterviewUuid: async (interviewUuid: string): Promise<Answer[]> => {
    const response = await axiosInstance.get(`/interviews/${interviewUuid}/answers`);
    return response.data;
  },

  submit: async (interviewUuid: string, answer: {
    question_id: string;
    content: string;
    recorder: string;
    frames: string;
    duration: number;
    metadata?: { [key: string]: string };
  }): Promise<Answer> => {
    const response = await axiosInstance.post(`/interviews/${interviewUuid}/answers`, answer);
    return response.data;
  },

  update: async (answerUuid: string, answer: {
    content: string;
    recorder: string;
    frames: string;
    duration: number;
    metadata?: { [key: string]: string };
  }): Promise<Answer> => {
    const response = await axiosInstance.put(`/answers/${answerUuid}`, answer);
    return response.data;
  }
};

// Proctor API calls for security monitoring
export interface ProctorSession {
  interview_uuid: string;
  session_id: string;
  video_url?: string;
  security_events: SecurityEvent[];
  screen_resolution: string;
  device_info: string;
  browser_info: string;
  started_at: string;
  completed_at?: string;
  date_created: string;
  is_active: boolean;
}

export interface SecurityEvent {
  type: string;
  timestamp: string;
  details: string;
  severity: string;
  process_name?: string;
}

export interface ProctorAlert {
  interview_uuid: string;
  session_id: string;
  event: SecurityEvent;
  timestamp: string;
}

export const proctorAPI = {
  createSession: async (interviewUuid: string, sessionData: {
    screen_resolution: string;
    device_info: string;
    browser_info: string;
  }): Promise<ProctorSession> => {
    const response = await axiosInstance.post(`/interviews/${interviewUuid}/proctor`, sessionData);
    return response.data;
  },

  updateSession: async (interviewUuid: string, updates: {
    security_events?: SecurityEvent[];
    video_url?: string;
    completed_at?: string;
  }): Promise<{ success: boolean }> => {
    const response = await axiosInstance.put(`/interviews/${interviewUuid}/proctor`, updates);
    return response.data;
  },

  getSession: async (interviewUuid: string): Promise<ProctorSession> => {
    const response = await axiosInstance.get(`/interviews/${interviewUuid}/proctor`);
    return response.data;
  },

  sendAlert: async (alert: ProctorAlert): Promise<{ success: boolean; alert_id: string; timestamp: string }> => {
    const response = await axiosInstance.post('/proctor/alert', alert);
    return response.data;
  }
};

// Video API calls for recording upload
export interface VideoUploadRequest {
  interview_uuid: string;
  video_type: 'screen' | 'webcam' | 'merged';
  video_data: string; // base64 encoded WebM data
  file_name?: string;
}

export interface VideoUploadResponse {
  success: boolean;
  video_url?: string;
  video_id: string;
  message?: string;
  error?: string;
}

export interface PresignedURLRequest {
  interview_uuid: string;
  video_type: 'screen' | 'webcam' | 'merged';
  file_size: number;
  content_type: string;
}

export interface PresignedURLResponse {
  success: boolean;
  upload_url: string;
  video_id: string;
  final_url: string;
  expires_in: number;
  error?: string;
}

export const videoAPI = {
  uploadVideo: async (interviewUuid: string, videoData: VideoUploadRequest): Promise<VideoUploadResponse> => {
    const response = await axiosInstance.post(`/interviews/${interviewUuid}/video/upload`, videoData);
    return response.data;
  },

  getPresignedURL: async (interviewUuid: string, presignedData: PresignedURLRequest): Promise<PresignedURLResponse> => {
    const response = await axiosInstance.post(`/interviews/${interviewUuid}/video/presigned`, presignedData);
    return response.data;
  }
};

// User Profile Interface (matching backend UserResponse)
export interface UserProfile {
  email: string;
  name: string;
  createdAt: string;
  lastLogin?: string;
  subscriptionId?: string;
  subscriptionStatus?: string;
  paymentStatus?: string;
  planId?: string;
  subscriptionStart?: string;
  subscriptionEnd?: string;
  // Usage information
  monthlyUsage: number;      // Current month's interview count
  monthlyLimit: number;      // Monthly interview limit based on plan
  remainingUsage: number;    // Remaining interviews this month
}

// User Management API
export const userAPI = {
  getProfile: async (): Promise<UserProfile> => {
    const response = await axiosInstance.get('/user');
    return response.data;
  },
  
  updateProfile: async (updates: { name?: string }): Promise<{ message: string }> => {
    const response = await axiosInstance.put('/user', updates);
    return response.data;
  },
};

// Subscription Management API  
export const subscriptionAPI = {
  createCheckoutSession: async (plan: 'pro' | 'premium'): Promise<{ url: string }> => {
    const response = await axiosInstance.post('/create-checkout-session', { plan });
    return response.data;
  },
  
  cancelSubscription: async (): Promise<{ message: string }> => {
    const response = await axiosInstance.post('/cancel-subscription');
    return response.data;
  },
};

// Interview Results Types
export interface InterviewResults {
  interview: {
    uuid: string;
    candidateName: string;
    candidateEmail: string;
    status: string;
    startedAt: string;
    completedAt: string;
    duration: number;
    // Privacy expiration field
    privacy_expired_at?: string;
    // Video recording URLs
    screen_recording_url?: string;
    webcam_recording_url?: string;
    merged_video_url?: string;
  };
  test: {
    uuid: string;
    title: string;
    description: string;
    questionsCount: number;
  };
  answers: Answer[];
  summary: {
    interview_uuid: string;
    answers: Answer[];
    completion_rate: number;
    total_questions: number;
    answered_questions: number;
  };
}

// Results API for interview analysis and review
export const resultsAPI = {
  getInterviewResults: async (interviewUuid: string): Promise<InterviewResults> => {
    if (!interviewUuid || interviewUuid === 'undefined' || interviewUuid === 'null') {
      throw new Error('Invalid interview UUID provided');
    }
    
    // This endpoint returns the complete interview results with analytics
    const response = await axiosInstance.get(`/interviews/${interviewUuid}/answers`);
    
    // Handle both wrapped and direct response structures
    if (response.data.interview && response.data.test) {
      // Nested structure (wrapped in .data)
      return response.data;
    } else if (response.data && response.data.interview && response.data.answers) {
      // Direct structure - backend returns InterviewResults directly
      return response.data;
    } else {
      // Fallback: construct the results from individual API calls if needed
      throw new Error('Backend should return complete interview results object');
    }
  }
};

// Infractions API for security violation management

// Standard infraction types
export type InfractionType = 
  // Security violations
  | 'window_focus_lost'
  | 'prohibited_app_detected'
  | 'multiple_monitors_detected'
  | 'display_change_detected'
  | 'clipboard_access'
  | 'network_activity'
  // Monitoring photos
  | 'webcam_monitoring'
  | 'screenshot_upload'
  // Legacy types (for backwards compatibility)
  | 'security_event'
  | 'window-focus'
  | 'prohibited-app'
  | 'multiple-monitors';

// Standardized metadata schema for infractions
export interface InfractionMetadata {
  // Core identification
  violation_id?: string;           // Unique violation ID for cross-referencing
  
  // Capture context
  captureReason?: 'interview_start' | 'interview_end' | 'time_interval' | 'violation_triggered';
  captureMethod?: 'webcam' | 'electron-screenshot' | 'general_interview_monitoring';
  
  // Classification flags
  is_routine_monitoring?: 'true' | 'false'; // Backend-set classification flag
  
  // Event context (for violations)
  eventType?: 'question_milestone' | 'security_violation' | 'system_event';
  
  // Additional context
  questionId?: string;             // Associated question ID if applicable
  timestamp?: string;              // Original capture timestamp
  source?: 'client' | 'proctor';  // Source of the evidence
}

export interface Infraction {
  interview_uuid: string;
  sk: string;
  timestamp: string;
  type: InfractionType; // Strongly typed infraction type
  description: string;
  evidence_r2_key?: string;
  evidence_type?: 'screenshot' | 'video';
  evidence_size?: number;
  duration?: number;
  metadata?: InfractionMetadata; // Strongly typed metadata
  explanation?: string; // Candidate explanation (150 chars max)
  explanation_date?: string; // When explanation was submitted
  ttl: number;
  date_created: string;
}

export interface InfractionUploadRequest {
  type: InfractionType; // Strongly typed infraction type
  description: string;
  evidence_type?: 'screenshot' | 'video';
  evidence_data?: string; // base64 encoded file data
  duration?: number;
  metadata?: InfractionMetadata; // Strongly typed metadata
}

export interface InfractionResponse {
  success: boolean;
  infraction_id: string;
  evidence_url?: string;
  message?: string;
  error?: string;
}

export interface InfractionEvidenceResponse {
  success: boolean;
  evidence_url: string;
  evidence_type: string;
  file_size: number;
  expires_in: number;
}

export const infractionsAPI = {
  uploadInfraction: async (interviewUuid: string, infraction: InfractionUploadRequest): Promise<InfractionResponse> => {
    if (!interviewUuid || interviewUuid === 'undefined' || interviewUuid === 'null') {
      throw new Error('Invalid interview UUID provided');
    }
    
    const response = await axiosInstance.post(`/interviews/${interviewUuid}/infractions`, infraction);
    return response.data;
  },

  getInfractions: async (interviewUuid: string, filters?: {
    limit?: number;
    category?: string;
    severity?: string;
    start_time?: string;
    end_time?: string;
    has_evidence?: boolean;
  }): Promise<Infraction[]> => {
    if (!interviewUuid || interviewUuid === 'undefined' || interviewUuid === 'null') {
      throw new Error('Invalid interview UUID provided');
    }

    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const url = `/interviews/${interviewUuid}/infractions${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await axiosInstance.get(url);
    return response.data;
  },

  getInfractionEvidence: async (infractionId: string, interviewUuid?: string): Promise<InfractionEvidenceResponse> => {
    if (!infractionId || infractionId === 'undefined' || infractionId === 'null') {
      throw new Error('Invalid infraction ID provided');
    }
    
    // Include interview UUID for efficient backend lookup
    const params = interviewUuid ? `?interview_uuid=${encodeURIComponent(interviewUuid)}` : '';
    const response = await axiosInstance.get(`/infractions/${infractionId}/evidence${params}`);
    return response.data;
  }
};

// Unlock request API calls
export const unlockAPI = {
  getUnlockStatus: async (interviewUuid: string): Promise<{
    status: 'none' | 'pending' | 'approved' | 'denied';
    unlock_requested: boolean;
    unlock_approved: boolean;
    requested_at?: string;
    approved_at?: string;
    questions_seen_ids: string[];
    questions_seen_count: number;
  }> => {
    if (!interviewUuid || interviewUuid === 'undefined' || interviewUuid === 'null') {
      throw new Error('Invalid interview UUID provided');
    }
    const response = await axiosInstance.get(`/interviews/${interviewUuid}/unlock-status`);
    return response.data;
  },

  cloneInterview: async (interviewUuid: string, replacementQuestions: Array<{
    old_question_id: string;
    new_content: string;
  }>): Promise<{
    message: string;
    new_interview_uuid: string;
    questions_replaced: number;
  }> => {
    if (!interviewUuid || interviewUuid === 'undefined' || interviewUuid === 'null') {
      throw new Error('Invalid interview UUID provided');
    }
    const response = await axiosInstance.post(`/interviews/${interviewUuid}/clone`, {
      replacement_questions: replacementQuestions
    });
    return response.data;
  },

  approveUnlock: async (interviewUuid: string): Promise<{
    message: string;
  }> => {
    if (!interviewUuid || interviewUuid === 'undefined' || interviewUuid === 'null') {
      throw new Error('Invalid interview UUID provided');
    }
    const response = await axiosInstance.post(`/interviews/${interviewUuid}/unlock-approve`);
    return response.data;
  }
};